import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryOptions,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import type {
  CreateClubNoteModel,
  ClubNoteDTO,
  ClubNotesQueryParams,
} from "@membentry/shared-api";
import type { ApiResponse } from "@membentry/shared-types";
import { getNextPageFromMeta } from "../utils/infinite-pagination";

export const MY_CLUB_NOTES_QUERY_KEY = "my-club-notes";
export const TODAY_CLUB_NOTES_QUERY_KEY = "my-club-notes-today";

type MutationSuccessHandler<TData, TVariables> = NonNullable<
  UseMutationOptions<TData, Error, TVariables>["onSuccess"]
>;

const withOnSuccess = <TData, TVariables>(
  base: MutationSuccessHandler<TData, TVariables>,
  extra?: MutationSuccessHandler<TData, TVariables>,
): MutationSuccessHandler<TData, TVariables> => {
  return (data, variables, onMutateResult, context) => {
    base(data, variables, onMutateResult, context);
    return extra?.(data, variables, onMutateResult, context);
  };
};

export function useAddClubNoteMutation(
  client: AxiosInstance,
  clubId: number,
  options?: Omit<
    UseMutationOptions<ApiResponse<ClubNoteDTO>, Error, CreateClubNoteModel>,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateClubNoteModel) => {
      const { addClubNote } = await import("@membentry/shared-api");
      return addClubNote(client, clubId, data);
    },
    onSuccess: withOnSuccess(() => {
      queryClient.invalidateQueries({ queryKey: [TODAY_CLUB_NOTES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [MY_CLUB_NOTES_QUERY_KEY] });
    }, options?.onSuccess),
    ...options,
  });
}

export function useGetClubNotes(
  client: AxiosInstance,
  params: ClubNotesQueryParams = {},
  options?: Omit<
    UseQueryOptions<ApiResponse<ClubNoteDTO[]>>,
    "queryKey" | "queryFn"
  >,
) {
  const { startDateUtc, endDateUtc, search = "", page = 0, pageSize = 10 } = params;

  return useQuery<ApiResponse<ClubNoteDTO[]>>({
    queryKey: [MY_CLUB_NOTES_QUERY_KEY, startDateUtc, endDateUtc, search, page, pageSize],
    queryFn: async () => {
      const { getMyClubNotes } = await import("@membentry/shared-api");
      return getMyClubNotes(client, { startDateUtc, endDateUtc, search, page, pageSize });
    },
    ...options,
  });
}

export function useGetTodaysClubNotes(
  client: AxiosInstance,
  options?: Omit<
    UseQueryOptions<ApiResponse<ClubNoteDTO[]>>,
    "queryKey" | "queryFn"
  >,
) {
  return useGetPaginatedTodaysClubNotes(client, { page: 0, pageSize: 10 }, options);
}

export function useGetPaginatedTodaysClubNotes(
  client: AxiosInstance,
  params: Omit<ClubNotesQueryParams, "startDateUtc" | "endDateUtc"> = {},
  options?: Omit<
    UseQueryOptions<ApiResponse<ClubNoteDTO[]>>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<ApiResponse<ClubNoteDTO[]>>({
    queryKey: [TODAY_CLUB_NOTES_QUERY_KEY, params.search, params.page, params.pageSize],
    queryFn: async () => {
      const { getTodaysClubNotes } = await import("@membentry/shared-api");
      return getTodaysClubNotes(client, params);
    },
    ...options,
  });
}

export function useGetInfiniteTodaysClubNotes(
  client: AxiosInstance,
  params: Omit<ClubNotesQueryParams, "startDateUtc" | "endDateUtc" | "page"> = {},
  options?: Omit<
    UseInfiniteQueryOptions<
      ApiResponse<ClubNoteDTO[]>,
      Error,
      InfiniteData<ApiResponse<ClubNoteDTO[]>>,
      readonly unknown[],
      number
    >,
    "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam"
  >,
) {
  const { search = "", pageSize = 10 } = params;

  return useInfiniteQuery<
    ApiResponse<ClubNoteDTO[]>,
    Error,
    InfiniteData<ApiResponse<ClubNoteDTO[]>>,
    readonly unknown[],
    number
  >({
    queryKey: [TODAY_CLUB_NOTES_QUERY_KEY, "infinite", search, pageSize],
    queryFn: async ({ pageParam = 0 }) => {
      const { getTodaysClubNotes } = await import("@membentry/shared-api");
      return getTodaysClubNotes(client, {
        search,
        page: pageParam as number,
        pageSize,
      });
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextPageFromMeta(lastPage, allPages.length, pageSize),
    initialPageParam: 0,
    ...options,
  });
}

export function useGetMyNotesForClub(
  client: AxiosInstance,
  clubId: number,
  params: ClubNotesQueryParams = {},
) {
  const { startDateUtc, endDateUtc, search = "", page = 0, pageSize = 10 } = params;

  return useQuery<ApiResponse<ClubNoteDTO[]>>({
    queryKey: [MY_CLUB_NOTES_QUERY_KEY, clubId, startDateUtc, endDateUtc, search, page, pageSize],
    queryFn: async () => {
      const { getMyNotesForClub } = await import("@membentry/shared-api");
      return getMyNotesForClub(client, clubId, { startDateUtc, endDateUtc, search, page, pageSize });
    },
    enabled: !!clubId,
  });
}

export function useGetTodaysNotesForClub(
  client: AxiosInstance,
  clubId: number,
  params: Omit<ClubNotesQueryParams, "startDateUtc" | "endDateUtc"> = {},
) {
  return useQuery<ApiResponse<ClubNoteDTO[]>>({
    queryKey: [MY_CLUB_NOTES_QUERY_KEY, clubId, "today", params.search, params.page, params.pageSize],
    queryFn: async () => {
      const { getTodaysNotesForClub } = await import("@membentry/shared-api");
      return getTodaysNotesForClub(client, clubId, params);
    },
    enabled: !!clubId,
  });
}

export function useGetInfiniteTodaysNotesForClub(
  client: AxiosInstance,
  clubId: number,
  params: Omit<ClubNotesQueryParams, "startDateUtc" | "endDateUtc" | "page"> = {},
  options?: Omit<
    UseInfiniteQueryOptions<
      ApiResponse<ClubNoteDTO[]>,
      Error,
      InfiniteData<ApiResponse<ClubNoteDTO[]>>,
      readonly unknown[],
      number
    >,
    "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam"
  >,
) {
  const { search = "", pageSize = 10 } = params;

  return useInfiniteQuery<
    ApiResponse<ClubNoteDTO[]>,
    Error,
    InfiniteData<ApiResponse<ClubNoteDTO[]>>,
    readonly unknown[],
    number
  >({
    queryKey: [MY_CLUB_NOTES_QUERY_KEY, clubId, "today", "infinite", search, pageSize],
    queryFn: async ({ pageParam = 0 }) => {
      const { getTodaysNotesForClub } = await import("@membentry/shared-api");
      return getTodaysNotesForClub(client, clubId, {
        search,
        page: pageParam as number,
        pageSize,
      });
    },
    getNextPageParam: (lastPage, allPages) =>
      getNextPageFromMeta(lastPage, allPages.length, pageSize),
    initialPageParam: 0,
    enabled: !!clubId,
    ...options,
  });
}
